import { ArrowRight } from 'lucide-react'
/**
 * (Re)generate-report confirm (#1, DETAIL-V2-DECISION-RULES.md rule 3). The
 * entry points (header kebab + failed-card CTA) only render with the
 * regenerate permission and a non-decided application; the resource endpoint
 * re-checks both and 409s decided applications.
 *
 * Two `mode`s, decided by {@link deriveReportAction}:
 * - `'regenerate'` — a finished report exists; re-running REPLACES it and
 *   flips status back to Processing. The copy warns so, because that is the
 *   conflict the decision-finality rules exist to prevent (danger CTA).
 * - `'generate'` — generation FAILED, so there is no report to replace; the
 *   copy is a plain "run it again" (dark CTA, no destructive warning).
 *
 * Posts `{ applicationId }` to /resources/screening-regenerate (v1's
 * fetcher contract, v1:4650-4660). Result handling uses the module's
 * ref-callback marker idiom (dialog-bits ActionResultMarker) — no useEffect.
 */
import { useRef } from 'react'
import { useFetcher, useRevalidator } from 'react-router'
import { toast } from 'sonner'
import type { ReportActionMode } from '#app/utils/application-detail-v2/derive'
import type { ApplicationSummary } from '#app/utils/application-detail-v2/types'
import { DetailDialog, PillButton } from './dialog-bits'

type RegenerateResult =
	| { success: true; applicationId: string; message?: string }
	| { error: string }

export function RegenerateDialog({
	open,
	onOpenChange,
	application,
	mode = 'regenerate',
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	application: ApplicationSummary
	/** 'generate' for a failed report (nothing to replace), 'regenerate' for a
	 * finished one (replaces it). Drives title/copy/CTA. */
	mode?: ReportActionMode
}) {
	const isGenerate = mode === 'generate'
	const fetcher = useFetcher<RegenerateResult>()
	const revalidator = useRevalidator()
	const busy = fetcher.state !== 'idle'

	const lastHandled = useRef<RegenerateResult | null>(null)
	const result = fetcher.state === 'idle' ? fetcher.data : undefined
	const marker =
		result && lastHandled.current !== result ? (
			<span
				hidden
				ref={() => {
					if (lastHandled.current === result) return
					lastHandled.current = result
					if ('success' in result && result.success) {
						toast.info(
							result.message ||
								`Report ${isGenerate ? 'generation' : 'regeneration'} started for ${application.applicantName}. Processing...`,
						)
						onOpenChange(false)
						void revalidator.revalidate()
					} else if ('error' in result) {
						toast.error(result.error)
					}
				}}
			/>
		) : null

	return (
		<>
			{marker}
			<DetailDialog
				open={open}
				onOpenChange={onOpenChange}
				title={isGenerate ? 'Generate Report' : 'Regenerate Report'}
				description={
					isGenerate
						? 'The previous attempt to generate this report failed. This runs ' +
							'screening again and moves the application to Processing while ' +
							'it works. Nothing more is needed from the applicant.'
						: 'This replaces the current report with a freshly generated one and ' +
							'moves the application back to Processing while it runs. The ' +
							'existing report data cannot be recovered afterwards.'
				}
				className="max-h-[90vh] overflow-y-auto [scrollbar-width:thin] sm:max-w-xl"
			>
				<div className="flex justify-end gap-3">
					<PillButton
						tone="outline"
						onClick={() => onOpenChange(false)}
						disabled={busy}
					>
						Cancel
					</PillButton>
					<PillButton
						tone={'dark'}
						disabled={busy}
						onClick={() => {
							void fetcher.submit(
								{ applicationId: application.id },
								{ method: 'post', action: '/resources/screening-regenerate' },
							)
						}}
					>
						{busy
							? 'Starting...'
							: isGenerate
								? 'Generate Report'
								: 'Regenerate Report'}
						<ArrowRight className="size-4" />
					</PillButton>
				</div>
			</DetailDialog>
		</>
	)
}
